import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {EventEmitter} from "nate-react-api-helpers";
import {Column, TID} from "../../../../misc/scroller/Table";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";

type comparable = number | string;

export const CheckboxExtParentContext = createContext({
    childContexts: new EventEmitter<{name: string, data: any[]}[]>(),
    autoCheckFx: (name: string, input: any): boolean => {
        return false;
    }
});

export function CheckboxExtParentProvider(props: PropsWithChildren<{
    autoCheckFx: (name: string, data: any) => boolean;
}>) {
    const emitter = useMemo(() => new EventEmitter<{name: string, data: any[]}[]>(), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const autoCheckFx = useMemo(() => props.autoCheckFx, []);

    return <CheckboxExtParentContext.Provider value={{
        childContexts: emitter,
        autoCheckFx,
    }}>
        {props.children}
    </CheckboxExtParentContext.Provider>
}

export const CheckboxExtContext = createContext({
    name: "-",
    data: new EventEmitter<any[]>(),
    allData: {current: null as null | any[]},
    setChecked: (value: any, getId: (input: any) => comparable, checked: boolean): void => {
        throw new Error("invalid provider");
    },
    selectAll: (tf: boolean) => {}
});

export function CheckboxExtProvider(props: PropsWithChildren<{
    name: string;
}>) {
    const name = props.name;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const emitter = useMemo(() => new EventEmitter<any[]>(), [name]);
    const allDataRef = useRef(null as null | any[]);
    const parent = useContext(CheckboxExtParentContext);
    const parentEmitter = parent.childContexts;

    useEffect(() => {
        const sub = emitter.subscribeAndFireLast((value) => {
            const parentList = (parentEmitter.lastValue || []).slice(0);

            if(!parentList.find(v => v.name === name)) {
                parentEmitter.emit([...parentList, {
                    name: name,
                    data: value || [],
                }]);

                return;
            }

            parentEmitter.emit(parentList.map(v => {
                if(v.name === name) {
                    return {
                        name: name,
                        data: value || [],
                    }
                }

                return v;
            }));
        })

        return () => {
            sub.cancel();
        }
    }, [emitter, parentEmitter, name]);

    const ctx = useMemo(() => {
        return {
            name: name,
            data: emitter,
            setChecked: (value: any, getId: (input: any) => comparable, checked: boolean) => {
                const list = (emitter.lastValue || []);
                const element = list.find(v => getId(v) === getId(value));

                if(checked) {
                    if(element) {
                        emitter.emit(list.slice(0));
                        return;
                    }

                    emitter.emit([...list, value]);
                    return
                }

                if(element) {
                    emitter.emit(list.filter(v => getId(v) !== getId(value)));
                }
            },
            allData: allDataRef,
            selectAll: (tf: boolean) => {
                if(allDataRef.current === null) {
                    throw new Error("All data not setup, see useCheckboxExtSetupData");
                }

                emitter.emit(tf ? allDataRef.current.slice(0) : []);
            },
        }
    }, [name, allDataRef, emitter]);

    return (
        <CheckboxExtContext.Provider value={ctx}>
            {props.children}
        </CheckboxExtContext.Provider>
    )
}

export function useCheckboxExtSetupData() {
    const {name, allData, setChecked} = useContext(CheckboxExtContext);
    const {autoCheckFx} = useContext(CheckboxExtParentContext);
    const cleanedUp = useRef(false);

    useEffect(() => {
        allData.current = []

        return () => {
            allData.current = null
            cleanedUp.current = true;
        }
    }, [allData])

    return useCallback(async (update: Promise<any[]>) => {
        const dt = await update;

        if(cleanedUp.current) return dt;

        allData.current = dt;
        allData.current
            .filter(d => autoCheckFx(name, d))
            .forEach(v => {
                setChecked(v, idSelector, true);
            });

        return dt;
    }, [autoCheckFx, allData, name, setChecked]);
}

export function CheckboxExtSelectorCol<T extends TID>(props?: {
    disabled?(input: T): boolean
}) {
    return {
        name: "",
        renderName: () => <div style={{marginTop: -4, marginBottom: -4}}>
            <CheckboxExtContent value="all" idSelector={idSelector} />
        </div>,
        render: o => <div style={{marginTop: -4, marginBottom: -4}}>
            <CheckboxExtContent value={o} idSelector={idSelector} disabled={props?.disabled && props.disabled(o)} />
        </div>,
        width: 50,
    } as Column<T>
}

const idSelector = (input: any) => input.id;

export function CheckboxExtContent(props: {
    value: any | "all";
    idSelector: (input: any) => comparable
    disabled?: boolean
}) {
    const [selected, onClick] = useIsCheckboxExtSelected(props.value, props.idSelector, !!props.disabled);

    return (
        <Checkbox size="small" checked={selected} onClick={onClick} disabled={props.disabled} />
    )
}

export function Checkbox(props: {
    checked: boolean
    onClick?: (e: any) => void,
    size?: "small";
    disabled?: boolean
    style?: any;
}) {
    if(props.disabled) {
        return (
            <div onClick={props.onClick} className={disabledStyle} style={props.style}>
                <svg viewBox="0 0 24 24" style={{color: "inherit", fill: "currentColor"}}>
                    <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                </svg>
            </div>
        );
    }

    if(props.checked) {
        return (
            <div onClick={props.onClick} className={checkedStyle} style={props.style}>
                <svg viewBox="0 0 24 24" style={{color: "inherit", fill: "currentColor"}}>
                    <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
            </div>
        );
    }

    return (
        <div onClick={props.onClick} className={unCheckedStyle} style={props.style}>
            <svg viewBox="0 0 24 24" style={{color: "inherit", fill: "currentColor"}}>
                <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
            </svg>
        </div>
    );
}

const checkedStyle = css({
    paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2,
    fontSize: "1.1rem",
    height: "1.4rem",

    "& svg": {
        width: "1em",
        height: "1em",
    }
})
const unCheckedStyle = css({
    paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2,
    fontSize: "1.1rem",
    height: "1.4rem",

    "& svg": {
        width: "1em",
        height: "1em",
    }
})
const disabledStyle = css({
    paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2,
    fontSize: "1.1rem",
    height: "1.4rem",

    "& svg": {
        width: "1em",
        height: "1em",

        "& path": {
            stroke: grey["300"],
            fill: grey["300"],
        }
    }
})

export function useIsCheckboxExtSelected(value: any | "all", idSelector: (input: any) => comparable, notCheckable: boolean = false): [boolean, (e: Event) => void] {
    const ctx = useContext(CheckboxExtContext);
    const [selected, setSelected] = useState(false);
    const {data, selectAll, setChecked} = ctx;

    const idSelectorRef = useRef(idSelector);
    idSelectorRef.current = idSelector;

    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    useEffect(() => {
        if(value === "all") {
            return;
        }

        if(notCheckable) {
            return;
        }

        const sub = data.subscribeAndFireLast(d => {
            const found = d.find(d => idSelectorRef.current(d) === idSelectorRef.current(value));
            const checked = !!found;

            if(selectedRef.current === checked) return;
            setSelected(checked);
        });

        return () => sub.cancel();
    }, [value, notCheckable, data]);

    const onClick = useCallback((e: Event) => {
        e.stopPropagation();

        if(value === "all") {
            selectAll(!selectedRef.current);
            setSelected(!selectedRef.current)
        } else {
            setChecked(value, idSelectorRef.current, !selectedRef.current);
        }
    }, [value, selectAll, setChecked]);

    return [selected, onClick];
}
